/**
 * Complete your stack — "Add N items to cart".
 *
 * Docs: docs/sections/complete-your-stack.md
 *
 * The per-card add-to-cart forms need no JavaScript: assets/cart.js delegates
 * on `form[action*="/cart/add"]` submits for the whole document. This element
 * exists only for the section-level button, which has no form of its own.
 *
 * The click handler is delegated from this element rather than bound to the
 * button, because the button lives inside <product-recommendations> and is
 * replaced wholesale when that element swaps in the Section Rendering API
 * response. This element is never replaced, so one listener survives the swap.
 *
 * @extends HTMLElement
 */
export class CompleteYourStack extends HTMLElement {
  constructor() {
    super();
    this.handleClick = this.handleClick.bind(this);
  }

  connectedCallback() {
    this.addEventListener('click', this.handleClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
  }

  /**
   * @param {MouseEvent} event
   */
  handleClick(event) {
    const button = event.target.closest('[data-add-all]');
    if (!button) {
      return;
    }

    event.preventDefault();
    this.addAll(button);
  }

  /**
   * Adds every addable card to the cart in one request.
   *
   * Uses Cart.addMany (assets/cart.js), which sends one /cart/add.js call with
   * an `items` array and reads the cart back once, instead of one add, one cart
   * read and one section render per product. Cart.addMany keeps the response
   * shape assets/component-cart-notification.js and
   * assets/component-data-layer.js expect, so both stay correct.
   *
   * All-or-nothing: if any product cannot be added (for example it is over the
   * available stock) none are, and the error is shown under the button.
   *
   * @param {HTMLButtonElement} button - The add-all button that was clicked.
   */
  async addAll(button) {
    if (!window.Cart) {
      return;
    }

    const items = Array.from(this.querySelectorAll('[data-variant-id]')).map((card) => ({
      id: Number(card.dataset.variantId),
      quantity: 1,
    }));
    if (!items.length) {
      return;
    }

    const label = button.querySelector('[data-add-all-text]');
    const errorElement = this.querySelector('[data-add-all-error]');
    const originalLabel = label ? label.textContent : null;

    if (errorElement) {
      errorElement.textContent = '';
    }
    button.disabled = true;
    if (label && button.dataset.addingLabel) {
      label.textContent = button.dataset.addingLabel;
    }

    try {
      await window.Cart.addMany(items, this);
    } catch (error) {
      if (errorElement) {
        errorElement.textContent = error.description || error.message;
      }
    } finally {
      button.disabled = false;
      if (label && originalLabel !== null) {
        label.textContent = originalLabel;
      }
    }
  }
}

if (!customElements.get('complete-your-stack')) {
  customElements.define('complete-your-stack', CompleteYourStack);
}
